import { useState, useEffect } from "react";
import { useNavigate, Link, Navigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import api, { formatApiError } from "../../lib/api";
import LogoMark from "../../components/site/LogoMark";
import { useLeads } from "../../hooks/useLeads";
import { LeadsFilterBar, LeadsTable, LeadDetailPanel } from "../../components/admin/LeadsUI";
import { LogOut, LayoutDashboard, Users, FileText, BarChart3, Search, Plus, Trash2, Edit3, Save, Star, MessageSquare } from "lucide-react";

export function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const nav = useNavigate();
  const { user, login } = useAuth();

  if (user && user !== false) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try { await login(email, password); toast.success("Welcome back."); nav("/admin"); }
    catch (err) { const m = formatApiError(err); setError(m); toast.error(m); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-[80vh] grid place-content-center container-wide py-16">
      <div className="card-soft p-8 w-full max-w-md">
        <LogoMark size={64} />
        <h1 className="font-heading text-2xl text-brand-ink mt-4">Admin Sign In</h1>
        <p className="text-sm text-brand-ink2 mt-1">Access the Physio Hyve dashboard.</p>
        <form onSubmit={submit} className="mt-6 space-y-3">
          <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email" required className="w-full rounded-xl border border-black/10 px-4 py-3 bg-white"/>
          <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password" required className="w-full rounded-xl border border-black/10 px-4 py-3 bg-white"/>
          {error && <div className="text-sm text-brand-terracotta" data-testid="admin-error">{error}</div>}
          <button disabled={loading} data-testid="admin-submit" className="btn-primary w-full justify-center">{loading ? "Signing in…" : "Sign In"}</button>
        </form>
        <div className="mt-6 text-xs text-brand-ink2"><Link to="/">← Back to website</Link></div>
      </div>
    </div>
  );
}

export function AdminLayout({ children }) {
  const { user, ready, logout } = useAuth();
  const location = useLocation();
  if (!ready) return <div className="p-10 text-brand-ink2">Loading…</div>;
  if (!user || user === false) return <Navigate to="/admin/login" replace state={{ from: location }} />;

  const nav = [
    { to: "/admin", label: "Dashboard", Icon: LayoutDashboard, end: true },
    { to: "/admin/leads", label: "Leads (CRM)", Icon: Users },
    { to: "/admin/blog", label: "Blog", Icon: FileText },
    { to: "/admin/reviews", label: "Reviews", Icon: MessageSquare },
    { to: "/admin/analytics", label: "Analytics", Icon: BarChart3 },
  ];
  return (
    <div className="min-h-screen grid md:grid-cols-[240px_1fr] bg-brand-bg-alt">
      <aside className="border-r border-black/5 bg-white p-5 md:min-h-screen">
        <Link to="/admin" className="flex items-center gap-2.5 mb-6">
          <LogoMark size={40} />
          <div className="font-heading text-brand-ink text-sm leading-tight">Physio Hyve Clinic<br/><span className="text-xs text-brand-ink2 font-normal">Admin</span></div>
        </Link>
        <nav className="space-y-1">
          {nav.map(({ to, label, Icon, end }) => {
            const active = end ? location.pathname === to : location.pathname.startsWith(to);
            return (
              <Link key={to} to={to} className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm ${active ? "bg-brand-cream text-brand-blue font-medium" : "text-brand-ink2 hover:bg-brand-cream/50"}`} data-testid={`admin-nav-${label.toLowerCase()}`}>
                <Icon size={16}/> {label}
              </Link>
            );
          })}
        </nav>
        <button onClick={logout} data-testid="admin-logout" className="mt-6 flex items-center gap-2 text-sm text-brand-ink2 hover:text-brand-amber px-3 py-2">
          <LogOut size={16}/> Sign out
        </button>
        <div className="mt-8 text-xs text-brand-ink2 px-3">Signed in as<br/><span className="text-brand-ink font-medium">{user.email}</span></div>
      </aside>
      <main className="p-5 md:p-8">{children}</main>
    </div>
  );
}

export function AdminDashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/leads/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);

  const cards = stats ? [
    { label: "Total leads", value: stats.total },
    { label: "Last 7 days", value: stats.recent_7_days },
    { label: "New (untouched)", value: stats.by_status?.new || 0 },
    { label: "Appointments booked", value: stats.by_status?.appointment_booked || 0 },
    { label: "Converted", value: stats.by_status?.converted || 0 },
  ] : [];

  return (
    <AdminLayout>
      <h1 className="font-heading text-3xl text-brand-ink">Dashboard</h1>
      <p className="text-brand-ink2 text-sm mt-1">Overview of leads, appointments and site activity.</p>
      <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="card-soft p-5" data-testid={`stat-${c.label.toLowerCase().replace(/\s/g,'-')}`}>
            <div className="text-xs text-brand-ink2 uppercase tracking-widest">{c.label}</div>
            <div className="font-heading text-3xl text-brand-ink mt-1">{c.value ?? "—"}</div>
          </div>
        ))}
      </div>
      {stats && (
        <div className="mt-8 grid md:grid-cols-2 gap-5">
          <div className="card-soft p-5">
            <div className="font-heading text-lg text-brand-ink mb-3">By category</div>
            <div className="space-y-2">
              {stats.by_category.length === 0 && <div className="text-sm text-brand-ink2">No data yet.</div>}
              {stats.by_category.map((r) => (
                <div key={r.category} className="flex justify-between text-sm"><span className="text-brand-ink2">{r.category}</span><span className="text-brand-ink font-medium">{r.count}</span></div>
              ))}
            </div>
          </div>
          <div className="card-soft p-5">
            <div className="font-heading text-lg text-brand-ink mb-3">By source</div>
            <div className="space-y-2">
              {stats.by_source.length === 0 && <div className="text-sm text-brand-ink2">No data yet.</div>}
              {stats.by_source.map((r) => (
                <div key={r.source} className="flex justify-between text-sm"><span className="text-brand-ink2">{r.source || "direct"}</span><span className="text-brand-ink font-medium">{r.count}</span></div>
              ))}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

const STATUSES = ["new","contacted","appointment_booked","visited","treatment_started","follow_up","converted","not_interested","closed"];

export function AdminLeads() {
  const { leads, q, setQ, status, setStatus, selected, setSelected, load, updateLead, removeLead } = useLeads();

  return (
    <AdminLayout>
      <h1 className="font-heading text-3xl text-brand-ink">Leads (CRM)</h1>
      <LeadsFilterBar q={q} setQ={setQ} status={status} setStatus={setStatus} onRefresh={load} />
      <div className="mt-6 grid lg:grid-cols-[1.6fr_1fr] gap-5">
        <LeadsTable leads={leads} selected={selected} onSelect={setSelected} onRemove={removeLead} />
        <LeadDetailPanel lead={selected} onUpdate={updateLead} />
      </div>
    </AdminLayout>
  );
}

export function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () => api.get("/admin/blog").then((r) => setPosts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const startCreate = () => setCreating({ title: "", slug: "", excerpt: "", content: "", category: "general", tags: [], featured_image: "", author: "Physio Hyve Team", published: false });

  const openEdit = async (id) => { const { data } = await api.get(`/admin/blog/${id}`); setEditing(data); };

  const saveNew = async () => {
    try { await api.post("/blog", creating); toast.success("Post created"); setCreating(null); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const saveEdit = async () => {
    try { const { id, ...rest } = editing; await api.patch(`/blog/${id}`, rest); toast.success("Post updated"); setEditing(null); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => { if (!window.confirm("Delete post?")) return; await api.delete(`/blog/${id}`); load(); };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl text-brand-ink">Blog</h1>
        <button onClick={startCreate} className="btn-primary" data-testid="blog-new"><Plus size={16}/> New post</button>
      </div>
      <div className="mt-6 card-soft overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-brand-bg-alt text-brand-ink2 text-xs uppercase tracking-widest">
            <tr><th className="text-left px-4 py-3">Title</th><th className="text-left px-4 py-3">Category</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">Updated</th><th></th></tr>
          </thead>
          <tbody>
            {posts.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-brand-ink2 text-center">No posts yet.</td></tr>}
            {posts.map((p) => (
              <tr key={p.id} className="border-t border-black/5 hover:bg-brand-bg-alt">
                <td className="px-4 py-3 text-brand-ink font-medium">{p.title}</td>
                <td className="px-4 py-3 text-brand-ink2">{p.category}</td>
                <td className="px-4 py-3">{p.published ? <span className="text-xs bg-brand-olive/10 text-brand-olive rounded-full px-2 py-1">Published</span> : <span className="text-xs bg-brand-cream text-brand-ink2 rounded-full px-2 py-1">Draft</span>}</td>
                <td className="px-4 py-3 text-brand-ink2 text-xs">{p.updated_at?.slice(0,10)}</td>
                <td className="px-4 py-3 text-right space-x-2">
                  <button onClick={() => openEdit(p.id)} className="text-brand-olive"><Edit3 size={14}/></button>
                  <button onClick={() => remove(p.id)} className="text-brand-terracotta"><Trash2 size={14}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && <BlogEditor value={creating} onChange={setCreating} onSave={saveNew} onClose={() => setCreating(null)}/>}
      {editing && <BlogEditor value={editing} onChange={setEditing} onSave={saveEdit} onClose={() => setEditing(null)}/>}
    </AdminLayout>
  );
}

function BlogEditor({ value, onChange, onSave, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start md:items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-3xl p-6 my-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-2xl text-brand-ink">Blog post</h2>
          <button onClick={onClose} className="text-brand-ink2" data-testid="blog-editor-close">Close</button>
        </div>
        <div className="grid gap-3">
          <input placeholder="Title" value={value.title} onChange={(e) => onChange({ ...value, title: e.target.value })} data-testid="blog-title" className="rounded-xl border border-black/10 px-3 py-2"/>
          <input placeholder="Slug (URL)" value={value.slug} onChange={(e) => onChange({ ...value, slug: e.target.value })} data-testid="blog-slug" className="rounded-xl border border-black/10 px-3 py-2"/>
          <input placeholder="Excerpt" value={value.excerpt} onChange={(e) => onChange({ ...value, excerpt: e.target.value })} data-testid="blog-excerpt" className="rounded-xl border border-black/10 px-3 py-2"/>
          <input placeholder="Featured image URL" value={value.featured_image || ""} onChange={(e) => onChange({ ...value, featured_image: e.target.value })} className="rounded-xl border border-black/10 px-3 py-2"/>
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Category" value={value.category} onChange={(e) => onChange({ ...value, category: e.target.value })} className="rounded-xl border border-black/10 px-3 py-2"/>
            <input placeholder="Author" value={value.author} onChange={(e) => onChange({ ...value, author: e.target.value })} className="rounded-xl border border-black/10 px-3 py-2"/>
          </div>
          <textarea placeholder="Content (Markdown-lite: ## for H2, - for list)" value={value.content} onChange={(e) => onChange({ ...value, content: e.target.value })} data-testid="blog-content" className="rounded-xl border border-black/10 px-3 py-2 min-h-[220px] font-mono text-sm"/>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!value.published} onChange={(e) => onChange({ ...value, published: e.target.checked })} data-testid="blog-published"/> Published</label>
          <button onClick={onSave} data-testid="blog-save" className="btn-primary justify-center"><Save size={16}/> Save</button>
        </div>
      </div>
    </div>
  );
}

export function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [goals, setGoals] = useState([]);

  const load = () =>
    api
      .get("/admin/analytics/dashboard")
      .then((r) => {
        setData(r.data);
        setGoals(r.data.conversion_goals || []);
      })
      .catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const toggleGoal = (name) => {
    setGoals((g) => (g.includes(name) ? g.filter((x) => x !== name) : [...g, name]));
  };

  const saveGoals = async () => {
    setSaving(true);
    try {
      await api.put("/admin/analytics/conversion-goals", { events: goals });
      toast.success("Conversion goals saved.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!data) {
    return (
      <AdminLayout>
        <div className="text-brand-ink2">Loading analytics…</div>
      </AdminLayout>
    );
  }

  const Tile = ({ label, value, sub }) => (
    <div className="card-soft p-5" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-xs uppercase tracking-widest text-brand-ink2">{label}</div>
      <div className="font-heading text-3xl text-brand-ink mt-1">{value}</div>
      {sub ? <div className="text-xs text-brand-ink2 mt-1">{sub}</div> : null}
    </div>
  );

  const maxTrend = Math.max(1, ...data.trend_last_7_days.map((d) => d.count));

  return (
    <AdminLayout>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl text-brand-ink">Analytics</h1>
          <p className="text-brand-ink2 text-sm mt-1">
            Live lead funnel — measured directly from your MongoDB, updated every reload. GA4 also
            receives every event for deeper drill-down.
          </p>
        </div>
      </div>

      {/* Big metric tiles */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Tile label="Leads Today" value={data.leads.today} />
        <Tile label="Leads This Week" value={data.leads.this_week} />
        <Tile label="Leads This Month" value={data.leads.this_month} />
        <Tile label="All-Time Leads" value={data.leads.all_time} />
      </div>

      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Tile label="Appointments Today" value={data.appointments.today} />
        <Tile label="Appointments Week" value={data.appointments.this_week} />
        <Tile label="Appointments Month" value={data.appointments.this_month} />
        <Tile label="Conversion Rate" value={`${data.conversion_rate_percent}%`} sub="Leads that reached appointment booked or beyond" />
      </div>

      {/* Trend chart */}
      <div className="mt-6 card-soft p-5">
        <div className="font-heading text-lg text-brand-ink">Leads — last 7 days</div>
        <div className="mt-4 grid grid-cols-7 gap-2 items-end h-32">
          {data.trend_last_7_days.map((d) => (
            <div key={d.day} className="flex flex-col items-center gap-1 h-full justify-end" data-testid={`trend-${d.day}`}>
              <div className="text-xs text-brand-ink font-medium">{d.count}</div>
              <div
                className="w-full rounded-t bg-brand-terracotta/80"
                style={{ height: `${(d.count / maxTrend) * 100}%`, minHeight: "4px" }}
              />
              <div className="text-xs text-brand-ink2 mt-1">{d.day}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Top source pages + UTM sources */}
      <div className="mt-6 grid md:grid-cols-2 gap-4">
        <div className="card-soft p-5">
          <div className="font-heading text-lg text-brand-ink mb-3">Top source pages</div>
          {data.top_source_pages.length === 0 ? (
            <div className="text-sm text-brand-ink2">No data yet.</div>
          ) : (
            <div className="space-y-2">
              {data.top_source_pages.map((p) => (
                <div key={p.page} className="flex justify-between text-sm">
                  <span className="text-brand-ink2 truncate">{p.page}</span>
                  <span className="text-brand-ink font-medium ml-3">{p.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="card-soft p-5">
          <div className="font-heading text-lg text-brand-ink mb-3">Top UTM sources</div>
          {data.top_utm_sources.length === 0 ? (
            <div className="text-sm text-brand-ink2">No data yet.</div>
          ) : (
            <div className="space-y-2">
              {data.top_utm_sources.map((s) => (
                <div key={s.source} className="flex justify-between text-sm">
                  <span className="text-brand-ink2 truncate">{s.source}</span>
                  <span className="text-brand-ink font-medium ml-3">{s.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* GA4 conversion goals */}
      <div className="mt-6 card-soft p-5" data-testid="conversion-goals-card">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="font-heading text-lg text-brand-ink">GA4 conversion goals</div>
            <p className="text-sm text-brand-ink2 mt-1 max-w-2xl">
              Tick the events that count as conversions. Then go to Google Analytics 4 →
              <span className="font-medium"> Admin → Events</span> → toggle "Mark as key event" for
              the same names so GA4 tracks conversions natively.
            </p>
          </div>
          <button
            onClick={saveGoals}
            disabled={saving}
            className="btn-primary"
            data-testid="save-conversion-goals"
          >
            <Save size={14}/> {saving ? "Saving…" : "Save goals"}
          </button>
        </div>

        <div className="mt-4 space-y-2">
          {data.known_ga4_events.map((ev) => (
            <label
              key={ev.name}
              className="flex items-center gap-3 p-3 rounded-xl border border-brand-cream hover:bg-brand-cream/30 cursor-pointer"
              data-testid={`goal-${ev.name}`}
            >
              <input
                type="checkbox"
                checked={goals.includes(ev.name)}
                onChange={() => toggleGoal(ev.name)}
                className="w-4 h-4"
              />
              <div className="flex-1">
                <div className="font-medium text-brand-ink text-sm">
                  {ev.label}
                  {ev.recommended ? (
                    <span className="ml-2 text-xs bg-brand-cream text-brand-olive rounded-full px-2 py-0.5">recommended</span>
                  ) : null}
                </div>
                <code className="text-xs text-brand-ink2">{ev.name}</code>
              </div>
            </label>
          ))}
        </div>
        <p className="text-xs text-brand-ink2 mt-3">{data.ga4_measurement_id_hint}</p>
      </div>
    </AdminLayout>
  );
}

export function AdminReviews() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ author_name: "", rating: 5, text: "", relative_time: "Recently", order: 0 });
  const [gmapUri, setGmapUri] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [rev, meta] = await Promise.all([
        api.get("/admin/curated-reviews"),
        api.get("/admin/curated-reviews/meta"),
      ]);
      setItems(rev.data || []);
      setGmapUri(meta.data?.google_maps_uri || "");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.author_name.trim() || !form.text.trim()) {
      toast.error("Please add both the patient's name and their review text.");
      return;
    }
    try {
      await api.post("/admin/curated-reviews", form);
      toast.success("Review added.");
      setForm({ author_name: "", rating: 5, text: "", relative_time: "Recently", order: 0 });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    try {
      await api.delete(`/admin/curated-reviews/${id}`);
      toast.success("Deleted.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveMeta = async () => {
    try {
      await api.put("/admin/curated-reviews/meta", { google_maps_uri: gmapUri });
      toast.success("Google Maps URL saved.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl text-brand-ink">Curated Reviews</h1>
          <p className="text-brand-ink2 text-sm mt-1">
            Add hand-picked patient reviews that appear on the homepage in Google-style cards.
          </p>
        </div>
      </div>

      {/* Google Maps URL */}
      <div className="mt-6 card-soft p-5">
        <div className="font-heading text-lg text-brand-ink">"See all on Google" link (optional)</div>
        <p className="text-sm text-brand-ink2 mt-1">
          Paste the Google Maps URL for your Physio Hyve Clinic listing. Adds a "See all on Google" button next to the reviews section.
        </p>
        <div className="mt-3 flex gap-2">
          <input
            value={gmapUri}
            onChange={(e) => setGmapUri(e.target.value)}
            placeholder="https://maps.app.goo.gl/... or https://g.co/kgs/..."
            className="flex-1 rounded-xl border border-black/10 px-4 py-2.5 bg-white"
            data-testid="admin-reviews-gmap-input"
          />
          <button onClick={saveMeta} className="btn-primary" data-testid="admin-reviews-gmap-save">
            <Save size={14}/> Save
          </button>
        </div>
      </div>

      {/* Add new */}
      <form onSubmit={submit} className="mt-6 card-soft p-5 space-y-3" data-testid="admin-reviews-form">
        <div className="font-heading text-lg text-brand-ink">Add a review</div>
        <div className="grid md:grid-cols-2 gap-3">
          <input required value={form.author_name} onChange={(e) => setForm({ ...form, author_name: e.target.value })} placeholder="Patient name (e.g. Sunita R.)" className="rounded-xl border border-black/10 px-4 py-2.5 bg-white" data-testid="review-author"/>
          <input value={form.relative_time} onChange={(e) => setForm({ ...form, relative_time: e.target.value })} placeholder="Relative time (e.g. 2 months ago)" className="rounded-xl border border-black/10 px-4 py-2.5 bg-white" data-testid="review-time"/>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-sm text-brand-ink2">Rating:</label>
          {[1,2,3,4,5].map((n) => (
            <button
              type="button"
              key={n}
              onClick={() => setForm({ ...form, rating: n })}
              className="p-1"
              data-testid={`review-star-${n}`}
              aria-label={`Set rating to ${n}`}
            >
              <Star size={20} className={n <= form.rating ? "fill-brand-amber text-brand-amber" : "text-brand-cream"} />
            </button>
          ))}
          <input type="number" min="0" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value || "0", 10) })} placeholder="Order" className="w-24 rounded-xl border border-black/10 px-3 py-2 bg-white text-sm ml-auto" data-testid="review-order" title="Lower shows first"/>
        </div>
        <textarea required value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder="Review text — paste it from Google as-is." rows={4} className="w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white" data-testid="review-text"/>
        <button className="btn-primary" data-testid="review-submit"><Plus size={14}/> Add review</button>
      </form>

      {/* Existing */}
      <div className="mt-6 card-soft p-5">
        <div className="font-heading text-lg text-brand-ink mb-3">Live on homepage ({items.length})</div>
        {loading ? (
          <div className="text-sm text-brand-ink2">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-brand-ink2">No curated reviews yet. Add your first review above.</div>
        ) : (
          <div className="space-y-3">
            {items.map((r) => (
              <div key={r.id} className="border border-brand-cream rounded-xl p-4" data-testid={`admin-review-${r.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-medium text-brand-ink flex items-center gap-2">
                      {r.author_name}
                      <span className="flex items-center gap-0.5">
                        {[1,2,3,4,5].map((n) => (
                          <Star key={n} size={12} className={n <= r.rating ? "fill-brand-amber text-brand-amber" : "text-brand-cream"} />
                        ))}
                      </span>
                    </div>
                    <div className="text-xs text-brand-ink2 mt-0.5">{r.relative_time} · order: {r.order}</div>
                    <p className="text-sm text-brand-ink2 mt-2">{r.text}</p>
                  </div>
                  <button onClick={() => remove(r.id)} className="text-brand-terracotta hover:text-red-700 text-sm inline-flex items-center gap-1" data-testid={`review-delete-${r.id}`}>
                    <Trash2 size={14}/> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

